import { createContext, ReactElement, useReducer } from "react"
import albumsReducer, { initialAlbumsState } from "../store/reducers/albums"
import axios from "utils/axios"
import { FETCH_ALBUMS, UPDATE_ALBUM_TAKEDOWN_STATUS } from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase, mapToCamelCase, mapToSnakeCase, toSnakeCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import { getFilterText, toastMessage } from "../utils"
import { Album, AlbumsContextType } from "../types/albums"
import { JobReport } from "types/constants"

const AlbumsContext = createContext<AlbumsContextType | null>(null)

export const AlbumsProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(albumsReducer, initialAlbumsState)

  const fetchAlbums = async (limit: number, offset: number, filter?: any, orderBy?: string) => {
    const tagsIncludedParam = filter.tagsIncluded?.length > 0 ? `&tags_included=${filter.tagsIncluded.join(",")}` : ""
    const tagsExcludedParam = filter.tagsExcluded?.length > 0 ? `&tags_excluded=${filter.tagsExcluded.join(",")}` : ""
    const filteredFilter = { ...filter, tagsIncluded: undefined, tagsExcluded: undefined }
    try {
      const response = await axios(
        `/albums/?limit=${limit}&offset=${offset}${getFilterText(
          filteredFilter
        )}${tagsIncludedParam}${tagsExcludedParam}${orderBy ? `&order_by=${orderBy}` : ""}`,
        { method: "get" }
      )

      dispatch({
        type: FETCH_ALBUMS,
        payload: { ...state, paginatedAlbums: mapPaginatedDataToCamelCase<PaginatedData<Album>>(response.data) }
      })

      return response.data?.results
    } catch (err) {
      toastMessage(`Error fetching albums ${err}`, "error")
      return []
    }
  }

  const updateAlbumStatus = async (album: Album) => {
    const response = await axios(`/albums/update_status/`, {
      method: "put",
      data: { id: album.id, takedown: album.takedown, create_offer: album.createOffer }
    })

    const updatedResults = [
      ...state.paginatedAlbums.results.map((result) => {
        if (result.id === album.id) {
          result.takedown = album.takedown
          result.createOffer = album.createOffer
        }
        return result
      })
    ]

    dispatch({
      type: UPDATE_ALBUM_TAKEDOWN_STATUS,
      payload: { ...state, paginatedAlbums: { ...state.paginatedAlbums, results: updatedResults } }
    })

    return response.data.success
  }

  const updateAlbum = async (albumToUpdate: Album, keyList: string[]) => {
    const keys = keyList.map((key) => toSnakeCase(key)).join(",")
    const response = await axios(`/albums/update_key/?keys=${keys}`, {
      method: "put",
      data: mapToSnakeCase(albumToUpdate)
    })

    if (!response.data.success) {
      toastMessage("Error saving album", "error")
      return
    }

    const results = [...state.paginatedAlbums.results].map((album: Album) => {
      if (album.id === albumToUpdate.id) {
        for (let i = 0; i < keyList.length; i++) {
          const _key = keyList[i]
          // @ts-ignore
          album[_key] = albumToUpdate[_key]
        }
      }
      return album
    })
    toastMessage("Successfully saved track", "success")

    dispatch({
      type: UPDATE_ALBUM_TAKEDOWN_STATUS,
      payload: { ...state, paginatedAlbums: { ...state.paginatedAlbums, results } }
    })

    return response.data.success
  }

  const updateAlbumCreateOfferList = async (idList: number[], createOffer: boolean) => {
    const response = await axios(`/albums/update_create_offer_list/`, {
      method: "put",
      data: { id_list: idList, create_offer: createOffer }
    })

    const updatedResults = [
      ...state.paginatedAlbums.results.map((result) => {
        if (idList.includes(result.id)) {
          result.createOffer = createOffer
        }
        return result
      })
    ]

    dispatch({
      type: UPDATE_ALBUM_TAKEDOWN_STATUS,
      payload: { ...state, paginatedAlbums: { ...state.paginatedAlbums, results: updatedResults } }
    })

    return response.data.success
  }

  const importChartMetricTimeline = async (
    albumId: number,
    artistId: number,
    convertListToSnackBar?: (logs: string[]) => React.ReactElement<any, string | React.JSXElementConstructor<any>>
  ): Promise<{ success: boolean; rateLimited?: boolean }> => {
    try {
      const resp = await axios(`/albums/import_chart_metric_timeline/?album_id=${albumId}&artist_id=${artistId}`, {
        method: "post"
      })

      if (!resp.data.result) {
        toastMessage(`Error using CM API. Please retry. If the error continues please contact.`, "error", 5000)
        return { success: false }
      }

      const jobReport: JobReport = mapToCamelCase(resp.data.result)
      let rateLimited = false
      if (jobReport.insertCount > 0 && convertListToSnackBar) {
        toastMessage(`Successfuly imported ${jobReport.insertCount} timeline data`, "success", 5000)
      }
      if (jobReport.errorLogs?.length > 0 && convertListToSnackBar) {
        const rateLimitHit = jobReport.errorLogs.some((log) => /429|rate limit/i.test(log))
        if (rateLimitHit) {
          rateLimited = true
        }
        toastMessage(convertListToSnackBar(jobReport.errorLogs), "error", 5000)
      }
      if (jobReport.warningLogs?.length > 0 && convertListToSnackBar) {
        toastMessage(convertListToSnackBar(jobReport.warningLogs), "warning", 5000)
      }
      return { success: true, rateLimited }
    } catch (err) {
      toastMessage(`Error using CM API. Please retry. If the error continues please contact.`, "error", 5000)
      return { success: false }
    }
  }

  return (
    <AlbumsContext.Provider
      value={{
        ...state,
        fetchAlbums,
        updateAlbumStatus,
        updateAlbumCreateOfferList,
        importChartMetricTimeline,
        updateAlbum
      }}
    >
      {children}
    </AlbumsContext.Provider>
  )
}

export default AlbumsContext
